import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ApiClient from '../../utils/ApiClient';

const FeedDetailModal = ({ feed, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('results');
  const [fullFeedData, setFullFeedData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && feed && feed.feed_submission_id) {
      setLoading(true);
      // Fetch the full feed data including results column
      ApiClient.get(`/api/feeds/${feed.job_id || feed.feed_submission_id}`)
        .then(response => {
          setFullFeedData(response.data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching feed details:', error);
          setLoading(false);
        });
    }
  }, [isOpen, feed]);
  
  if (!feed) return null;

  const getFormattedJson = () => {
    try {
      return JSON.stringify(feed, null, 2);
    } catch (e) {
      return String(feed);
    }
  };
  
  const getFormattedResults = () => {
    // Use the fullFeedData to get results
    const feedToUse = fullFeedData || feed;
    
    if (!feedToUse || !feedToUse.results) {
      if (loading) return "Loading results data...";
      return "No results data available";
    }
    
    try {
      // Try to parse the results as JSON
      const resultsObj = JSON.parse(feedToUse.results);
      return JSON.stringify(resultsObj, null, 2);
    } catch (e) {
      // If parsing fails, return the raw string
      return feedToUse.results;
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={onClose}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Feed Details
                  </Dialog.Title>
                  
                  {/* Tab navigation */}
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                      <button
                        className={`${
                          activeTab === 'results'
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        onClick={() => setActiveTab('results')}
                      >
                        Results Data
                      </button>
                      <button
                        className={`${
                          activeTab === 'raw'
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        onClick={() => setActiveTab('raw')}
                      >
                        Raw Feed Data
                      </button>
                    </nav>
                  </div>
                  
                  <div className="mt-4">
                    {activeTab === 'results' ? (
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-96 overflow-y-auto">
                        <pre className="text-sm text-gray-300 font-mono whitespace-pre">
                          {getFormattedResults()}
                        </pre>
                      </div>
                    ) : (
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-96 overflow-y-auto">
                        <pre className="text-sm text-gray-300 font-mono whitespace-pre">
                          {getFormattedJson()}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default FeedDetailModal;